import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { supabase } from '../../api/supabase';

const NavSection = ({ title, children }) => (
  <div className="sidebar-section">
    <div className="sidebar-section-title">{title}</div>
    {children}
  </div>
);

const SideLink = ({ to, label, end }) => (
  <NavLink to={to} end={end} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
    {label}
  </NavLink>
);

function useCostingPendingCount(skip, pathname) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (skip) return;
    supabase.from('invoices')
      .select('id', { count: 'exact', head: true })
      .neq('status', 'VOID')
      .eq('costing_finalized', false)
      .then(({ count: c }) => setCount(c || 0));
  }, [skip, pathname]); // refetch on every navigation so the badge stays in sync
  return count;
}

export default function Layout() {
  const { user, logout, isAgent, agentRefId, role, canSee } = useAuth();
  const { connected } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const costingCount = useCostingPendingCount(isAgent || !canSee('invoices'), location.pathname);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ overflow: 'hidden', height: 46 }}>
            <img src="/travelscape-logo.png" alt="Travelscape" style={{ width: 180, display: 'block', marginTop: -4 }} />
          </div>
          <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.5px', marginTop: 2 }}>TravelAcc Pro</div>
        </div>

        {isAgent ? (
          <>
            <NavSection title="My Portal">
              <SideLink to="/agent" label="My Sales" />
            </NavSection>
            <NavSection title="Operations">
              <SideLink to="/sale-quotations" label="Quotations" />
              <SideLink to="/quotations" label="Pro Forma Invoices" />
            </NavSection>
          </>
        ) : (
          <>
            {canSee('dashboard') && <NavSection title="Overview"><SideLink to="/" end label="Dashboard" /></NavSection>}

            {/* An admin/staff account that's also linked to an agent record (e.g. Lakmali)
                gets its own numbers alongside the full admin view — AgentRoute already
                allows this via agentRefId, this just makes it reachable. */}
            {agentRefId && <NavSection title="My Portal"><SideLink to="/agent" label="Agent View" /></NavSection>}

            {(canSee('clients') || canSee('suppliers') || canSee('agents')) && (
              <NavSection title="CRM">
                {canSee('clients')   && <SideLink to="/clients"   label="Clients" />}
                {canSee('suppliers') && <SideLink to="/suppliers" label="Suppliers" />}
                {canSee('agents')    && <SideLink to="/agents"    label="Agents" />}
              </NavSection>
            )}

            {(canSee('sale_quotations') || canSee('quotations') || canSee('invoices') || canSee('packages') || canSee('bookings') || canSee('tickets') || canSee('visas')) && (
              <NavSection title="Operations">
                {canSee('sale_quotations') && <SideLink to="/sale-quotations" label="Quotations" />}
                {canSee('quotations') && <SideLink to="/quotations" label="Pro Forma Invoices" />}
                {canSee('invoices')   && <SideLink to="/invoices"   label="Invoices" end />}
                {canSee('invoices') && costingCount > 0 && (
                  <NavLink to="/invoices/costing-pending" className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Costing Pending</span>
                    <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{costingCount}</span>
                  </NavLink>
                )}
                {canSee('packages')   && <SideLink to="/packages"   label="Packages" />}
                {canSee('bookings')   && <SideLink to="/bookings"   label="Bookings" />}
                {canSee('tickets')    && <SideLink to="/tickets"    label="Ticketing" />}
                {canSee('visas')      && <SideLink to="/visas"      label="Visas" />}
              </NavSection>
            )}

            {(canSee('bills') || canSee('receipt_vouchers') || canSee('payment_vouchers') || canSee('cheque_dashboard')) && (
              <NavSection title="Cash &amp; Bank">
                {canSee('accounts') && <SideLink to="/cash-bank" label="Cash & Bank" />}
                {canSee('bills')            && <SideLink to="/bills"                   label="Bills" />}
                {canSee('receipt_vouchers') && <SideLink to="/vouchers/receipt"        label="Receipt Vouchers" />}
                {canSee('receipt_vouchers') && <SideLink to="/vouchers/suspense"       label="Suspense Register" />}
                {canSee('payment_vouchers') && <SideLink to="/vouchers/payment"        label="Payment Vouchers" />}
                {canSee('cheque_dashboard') && <SideLink to="/vouchers/cheques"        label="Cheque Dashboard" />}
              </NavSection>
            )}

            {(canSee('ar') || canSee('ap') || canSee('journals') || canSee('accounts')) && (
              <NavSection title="Finance">
                {canSee('ar')       && <SideLink to="/finance/ar" label="Accounts Receivable" />}
                {canSee('ap')       && <SideLink to="/finance/ap" label="Accounts Payable" />}
                {canSee('journals') && <SideLink to="/journals"   label="Journal Entries" />}
                {canSee('accounts') && <SideLink to="/accounts"   label="Chart of Accounts" />}
              </NavSection>
            )}

            {(canSee('client_ledger') || canSee('supplier_ledger')) && (
              <NavSection title="Ledgers">
                {canSee('client_ledger')   && <SideLink to="/ledger/clients"   label="Client Ledger" />}
                {canSee('supplier_ledger') && <SideLink to="/ledger/suppliers" label="Supplier Ledger" />}
              </NavSection>
            )}

            {(canSee('trial_balance') || canSee('profit_loss') || canSee('balance_sheet') || canSee('commissions_ledger') || canSee('commission_setoff') || canSee('staff_performance') || canSee('agent_sales') || canSee('tickets_report')) && (
              <NavSection title="Reports">
                {canSee('trial_balance')      && <SideLink to="/finance/trial-balance" label="Trial Balance" />}
                {canSee('profit_loss')        && <SideLink to="/finance/profit-loss"   label="Profit & Loss" />}
                {canSee('balance_sheet')      && <SideLink to="/finance/balance-sheet" label="Balance Sheet" />}
                {canSee('commissions_ledger') && <SideLink to="/commissions/ledger"    label="Commissions" />}
                {canSee('commission_setoff')  && <SideLink to="/commissions/setoff"    label="Commission Set-Off" />}
                {canSee('staff_performance')  && <SideLink to="/agents/performance"    label="Staff Performance" />}
                {canSee('agent_sales')        && <SideLink to="/reports/agent-sales"   label="Agent Sales" />}
                {canSee('tickets_report')     && <SideLink to="/reports/tickets-issued" label="Tickets Issued" />}
              </NavSection>
            )}

            {(canSee('commission_table') || canSee('package_options') || canSee('users') || canSee('terms')) && (
              <NavSection title="Settings">
                {canSee('commission_table')   && <SideLink to="/commissions/table"            label="Commission Table" />}
                {canSee('package_options')    && <SideLink to="/settings/package-options"     label="Package Options" />}
                {canSee('users')              && <SideLink to="/users"                        label="Users & Roles" />}
                {canSee('terms')              && <SideLink to="/settings/terms"               label="Terms & Conditions" />}
                {canSee('terms')              && <SideLink to="/settings/company"             label="Company Details" />}
              </NavSection>
            )}
          </>
        )}

        <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>
          Travelscape Accounts <span style={{ fontFamily: 'monospace' }}>v1.7.0</span>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
            <span className="live-dot" style={{ background: connected ? 'var(--success, #22c55e)' : '#f59e0b' }} />
            {connected ? 'Connected' : 'Connecting...'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {user?.email?.split('@')[0]}
            </span>
            <button className="btn btn-secondary btn-sm" onClick={handleLogout}>Sign out</button>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
